import React, { useEffect, useState, useCallback } from 'react';
import { Card, Skeleton, message, Empty, List, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { getMyInspectionResponses } from '../utils/apiService';
import { formatDate } from '../utils/dateUtils';
import { getAllInspectionDrafts, deleteInspectionDraft, getInspectionDraft } from '../utils/inspectionCacheUtils';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const normalizeDateValue = (val) => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (val instanceof Date) return Number.isFinite(val.getTime()) ? val.toISOString() : null;
  if (typeof val === 'number') {
    const d = new Date(val);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  }
  return null;
};

const extractDraftMeta = (draft, source) => {
  const isServer = source === 'server';

  const templateName = String(draft?.inspectionName || 'Draft');
  const companyName = String(draft?.companyName || '');
  const registration = isServer ? String(draft?.vehicle?.registration || '') : '';
  const fleet = isServer ? String(draft?.vehicle?.fleet || '') : '';

  const raw = isServer ? draft?.createdDateTime : draft?.createdOn;
  const normalized = normalizeDateValue(raw);
  const formatted = normalized ? formatDate(normalized, true) : null;
  const startedOn = formatted && formatted !== 'Invalid date' ? formatted : 'Unknown';

  return { templateName, companyName, registration, fleet, startedOn };
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

const descriptionLabel = { fontWeight: 500, color: 'rgba(0, 0, 0, 0.65)' };
const descriptionValue = { fontWeight: 400, color: 'rgba(0, 0, 0, 0.45)' };

const DraftDescription = ({ meta }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {meta.companyName && (
      <div>
        <span style={descriptionLabel}>Company: </span>
        <span style={descriptionValue}>{meta.companyName}</span>
      </div>
    )}
    {meta.registration && (
      <div>
        <span style={descriptionLabel}>Registration: </span>
        <span style={descriptionValue}>{meta.registration}</span>
      </div>
    )}
    {meta.fleet && (
      <div>
        <span style={descriptionLabel}>Fleet: </span>
        <span style={descriptionValue}>{meta.fleet}</span>
      </div>
    )}
    <div>
      <span style={descriptionLabel}>Started on: </span>
      <span style={descriptionValue}>{meta.startedOn}</span>
    </div>
    <div style={{ ...descriptionValue, fontStyle: 'italic', marginTop: 2 }}>
      Tap to continue your draft
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const DraftInspection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [localDrafts, setLocalDrafts] = useState([]);
  const [isOffline, setIsOffline] = useState(false);

  const loadDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const online = navigator.onLine ?? true;
      setIsOffline(!online);

      if (online) {
        const res = await getMyInspectionResponses({ isCompleted: false, pageSize: 20, pageIndex: 0 });
        setDrafts(Array.isArray(res) ? res : res?.items ?? []);
      } else {
        setDrafts([]);
      }

      try {
        setLocalDrafts((await getAllInspectionDrafts()) ?? []);
      } catch (e) {
        console.error('Failed to load local inspection drafts:', e);
      }
    } catch (err) {
      console.error(err);
      if (navigator.onLine) message.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts, location.key]);

  /* ---- Local-draft click handler ---- */

  const handleLocalDraftClick = useCallback(async (draft) => {
    const removeDraft = () =>
      setLocalDrafts((prev) => prev.filter((x) => x.cacheKey !== draft.cacheKey));

    try {
      const stillExists = await getInspectionDraft(draft.cacheKey);
      if (!stillExists) {
        removeDraft();
        message.warning('This draft is no longer available');
        return;
      }
    } catch {
      removeDraft();
      message.warning('This draft is no longer available');
      return;
    }

    if (draft.responseId) {
      navigate(`/inspections/responses/${draft.responseId}`);
      return;
    }
    if (!draft.templateId) {
      message.warning('Template information missing for this draft');
      return;
    }
    navigate(`/inspections/templates/${draft.templateId}`, {
      state: { draftKey: draft.cacheKey },
    });
  }, [navigate]);

  /* ---- Delete local draft handler ---- */

  const handleDeleteLocalDraft = useCallback(async (e, cacheKey) => {
    e.stopPropagation();
    try {
      await deleteInspectionDraft(cacheKey);
      setLocalDrafts((prev) => prev.filter((x) => x.cacheKey !== cacheKey));
      message.success('Local draft deleted');
    } catch (err) {
      console.error('Failed to delete local draft:', err);
      message.error('Failed to delete local draft');
    }
  }, []);

  /* ---- Render ---- */

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button onClick={loadDrafts}>Refresh</Button>
      </div>

      {loading ? (
        <Skeleton active />
      ) : (
        <>
          {/* ---------- Server drafts ---------- */}
          <Card title="Saved Drafts" style={{ marginBottom: 16 }}>
            {isOffline ? (
              <Empty description="Offline - saved drafts are unavailable" />
            ) : drafts.length === 0 ? (
              <Empty description="No saved drafts available" />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={drafts}
                renderItem={(d) => {
                  const meta = extractDraftMeta(d, 'server');
                  return (
                    <List.Item
                      key={d.id}
                      onClick={() => navigate(`/inspections/responses/${d.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        title={meta.templateName}
                        description={<DraftDescription meta={meta} />}
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>

          {/* ---------- Local drafts ---------- */}
          <Card title="Local Drafts">
            {localDrafts.length === 0 ? (
              <Empty description="No local drafts available" />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={localDrafts}
                renderItem={(d) => {
                  const meta = extractDraftMeta(d, 'local');
                  return (
                    <List.Item
                      key={d.cacheKey}
                      onClick={() => handleLocalDraftClick(d)}
                      style={{ cursor: 'pointer' }}
                      actions={[
                        <Button
                          key="delete"
                          size="small"
                          onClick={(e) => handleDeleteLocalDraft(e, d.cacheKey)}
                        >
                          Delete
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={meta.templateName}
                        description={<DraftDescription meta={meta} />}
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default DraftInspection;
